import * as THREE from 'three';
import { FikoCizimYalin } from './FikoCizimYalin';
import { FikoImporterWRL } from './FikoImporter.WRL';
import { FikoImporterAPTCAT } from './FikoImporter.APTCAT';
import { FikoObje3D } from './FikoObje3D';
import { FikoAPTCAT } from './FikoThreeCam/FikoAPTCAD';
import { FikoImporterFikoOpGL } from './FikoImporter.FikoOpGL';
import { FikoAPTv1 } from './FikoThreeCam/FikoAPTV1';
import { FikoImporterAPTv1 } from './FikoImporter.APTv1';
import { FikoImporterFikoOpGLv1 } from './FikoImporter.FikoOpGLv1';
import { FikoOperasyon } from './FikoThreeCam/Fikoperasyon';
import { FikoOperasyonlar } from './FikoThreeCam/Fikoperasyonlar';
import { FikoCizimler } from './FikoThreeCam/FikoCizimler';
import { Fikogram } from './FikoThreeCam/Fikogram';
import { FikoGrupCizim } from './FikoGrupCizim';
import { FikoImporterSTP } from './FikoImporter.STP';
import { FikoImporterFikoJson } from './FikoImporter.FikoJson';
import { FikoImporterPointCloud, POINT_CLOUD_FORMATS } from './FikoImporter.PointCloud';
import { FikoEtiketler } from './FikoEtiketler';
import { FikoKonumlar } from './FikoKonumlar/FikoKonumlar';
import type { STPImportOptions } from './stp-import-dialog';

/** Tüm desteklenen dosya formatları */
export const ALL_SUPPORTED_FORMATS = [
    '.stp', '.step',                    // STEP/CAD
    '.wrl', '.wrl2', '.FikoWrl',        // VRML
    '.json', '.FikoJson',               // JSON
    '.apt', '.FikoApt', '.FikoAptv1',   // APT
    '.FikoOpGL', '.FikoOpGLv1',         // FikoOpGL
    '.FikoOp',                          // FikoOperasyon (JSON)
    '.FikoSahne',                       // FikoSahne (Komple sahne - gzip JSON)
    ...POINT_CLOUD_FORMATS              // Point Cloud: .ply, .xyz, .pts, .asc, .pcd, .obj
];

/** Dosya dialogunda gösterilecek formatlar (tüm desteklenen) */
export const FILE_DIALOG_FORMATS = [
    '.stp', '.step',                    // STEP/CAD
    '.wrl', '.wrl2', '.FikoWrl',        // VRML
    '.json', '.FikoJson',               // JSON
    '.apt', '.FikoApt', '.FikoAptv1',   // APT
    '.FikoOpGL', '.FikoOpGLv1',         // FikoOpGL
    '.FikoOp',                          // FikoOperasyon (JSON)
    '.FikoSahne',                       // FikoSahne (Komple sahne - gzip JSON)
    '.ply', '.xyz', '.pts', '.asc', '.pcd', '.obj', '.stl'  // Point Cloud & Mesh
];

export class FikoImporter {

    private stpImporter: FikoImporterSTP;

    /** Progress callback - yükleme ilerlemesi için */
    public onProgress?: (mesaj: string, progress: number) => void;

    /** Custom STP parser kullanmak mı yoksa FreeCAD API'si mi (default: true = custom parser) */
    public useCustomStpParser: boolean = false;

    constructor(public sahip: FikoObje3D = null) {
        this.stpImporter = new FikoImporterSTP(sahip);
    }

    /**
     * OCCT-WASM kullanımını aktif/pasif yapar
     * @param enabled true: OCCT ile yükle, false: FreeCAD API ile yükle
     */
    public setOCCTEnabled(enabled: boolean) {
        this.stpImporter.setOCCTEnabled(enabled);
    }

    /**
     * STP dosyasını FreeCAD API ile glTF'e çevirip yükler
     * @param dosya STP/STEP dosyası
     * @returns Promise<FikoCizimYalin>
     */
    public async stpDosyasiniYukle(dosya: File): Promise<FikoCizimYalin | any> {
        // onProgress callback'i stpImporter'a aktar
        this.stpImporter.onProgress = this.onProgress;
        return this.stpImporter.yukle(dosya);
    }


    /**
     * Dosya listesinden doğrudan import et (sürükle-bırak için)
     * @param dosyalar File[] dizisi
     */
    async dosyalardanYukle(dosyalar: File[]): Promise<any> {
        if (!dosyalar || dosyalar.length === 0) return null;

        // Desteklenen formatları filtrele
        const desteklenen = dosyalar.filter(d => {
            const ad = d.name.toLowerCase();
            return ALL_SUPPORTED_FORMATS.some(fmt => ad.endsWith(fmt.toLowerCase()));
        });

        if (desteklenen.length === 0) return null;

        return new Promise((resolve, reject) => {
            if (desteklenen.length === 1) {
                this.tekDosyaYukle(desteklenen[0], resolve, reject);
            } else {
                this.cokluDosyaYukle(desteklenen, resolve, reject);
            }
        });
    }

    /**
     * Dosya dialog'u ile dosya(lar) seç ve import et
     * @param filitre Kabul edilen dosya formatları
     * @param cokluSecim Birden fazla dosya seçimine izin ver (default: true)
     */
    async dosyadan(filitre: string = FILE_DIALOG_FORMATS.join(','), cokluSecim: boolean = true) {
        return new Promise((resolve, reject) => {

            const _this = this;
            let input = document.createElement("input");
            input.type = "file";
            // Windows uyumlu standart formatlar (custom formatlar da desteklenir)
            input.accept = filitre;
            // Çoklu dosya seçimi
            if (cokluSecim) {
                input.multiple = true;
            }
            input.onchange = async ($event: any) => {
                // console.log($event.target.files[0])
                if ($event.target.files && $event.target.files.length > 0) {
                    const dosyalar: File[] = Array.from($event.target.files);

                    // Tek dosya için eski davranış
                    if (dosyalar.length === 1) {
                        const dosya = dosyalar[0];
                        _this.tekDosyaYukle(dosya, resolve, reject);
                        return;
                    }

                    // Çoklu dosya import
                    _this.cokluDosyaYukle(dosyalar, resolve, reject);
                    return;
                }
            };

            input.click();
        });
    }

    /**
     * Tek dosya yükleme (eski davranış)
     */
    private tekDosyaYukle(dosya: File, resolve: (value: any) => void, reject: (reason: any) => void): void {
        const _this = this;

                    // Eğer onProgress callback yoksa, sahip objesinden almaya çalış
                    if (!_this.onProgress && _this.sahip?.sahne?.anaGosterim) {
                        const anaGosterim = _this.sahip.sahne.anaGosterim;

                        _this.onProgress = (mesaj: string, progress: number) => {
                            anaGosterim.yuklemeDurumu.aktif = true;
                            anaGosterim.yuklemeDurumu.mesaj = mesaj;
                            anaGosterim.yuklemeDurumu.progress = progress;

                            setTimeout(() => {
                                try {
                                    anaGosterim.CD.detectChanges();
                                } catch (e) {
                                    // CD destroyed olduğunda normal - sessiz geç
                                }
                            }, 0);

                            if (progress >= 100) {
                                setTimeout(() => {
                                    anaGosterim.yuklemeDurumu.aktif = false;
                                    try {
                                        anaGosterim.CD.detectChanges();
                                    } catch (e) {
                                        // CD destroyed olduğunda normal - sessiz geç
                                    }
                                }, 1000);
                            }
                        };
                    }

                    /** BİLGİ:  STP/STEP dosya importları */
                    if (dosya.name.toLowerCase().indexOf(".stp") > -1 || dosya.name.toLowerCase().indexOf(".step") > -1) {
                        // Progress callback'i importer'a aktar
                        _this.stpImporter.onProgress = _this.onProgress;

                        // Dialog servisini al (FikoThreeJsComponent'te DialogFiko olarak inject edilmiş)
                        const dialogService = _this.sahip?.sahne?.anaGosterim?.DialogFiko ?? null;

                        // Dialog ile veya varsayılan yöntemle import et
                        _this.processStpImport(dosya, dialogService, resolve, reject);
                    } else
                        /** BİLGİ:  Wrl Yeni Gruplu İmportları */
                        if (dosya.name.indexOf(".wrl2") > -1 || dosya.name.indexOf(".FikoWrl") > -1) {
                            const importer = new FikoImporterWRL(_this.sahip.sahne);
                            importer.onProgress = _this.onProgress;
                            importer.alDosyadan(dosya).then((_rslt: { yuzeyler: any, cizgiler: any, vertex: any }[]) => {

                                for (let i = 0; i < _rslt.length; i++) {
                                    const e = _rslt[i];
                                    const cizim = new FikoCizimYalin(_this.sahip.sahne, dosya.name.replace(".FikoWrl", "").replace(".wrl", ""), e.yuzeyler, e.cizgiler, e.vertex);
                                    cizim.name += "." + (i + 1)
                                    _this.sahip.ekle(cizim);
                                }


                                _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                _this.sahip.sahne.anaGosterim.ciz();

                                // 🚀 SPATIAL INDEX: Register edges for O(1) hover lookup
                                setTimeout(() => {
                                    if (_this.sahip?.sahne?.anaGosterim?.secililer) {
                                        _this.sahip.sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                                    }
                                }, 200);
                            })
                        } else                            /** BİLGİ:  Wrl İmportları */
                            if (dosya.name.indexOf(".FikoJson") > -1 || dosya.name.indexOf(".json") > -1) {
                                const importer = new FikoImporterFikoJson(_this.sahip.sahne);
                                importer.onProgress = _this.onProgress;
                                importer.alDosyadan1(dosya).then((_rslt: { yuzeyler: any, cizgiler: any, vertex: any }) => {
                                    const cizim = new FikoCizimYalin(_this.sahip.sahne, dosya.name.replace(".FikoJson", "").replace(".json", ""), _rslt.yuzeyler, _rslt.cizgiler, _rslt.vertex);
                                    _this.sahip.ekle(cizim);

                                    // Sahneyi güncelle
                                    if (_this.sahip?.sahne?.anaGosterim) {
                                        _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                        _this.sahip.sahne.anaGosterim.ciz();

                                        // 🚀 SPATIAL INDEX: Register edges for O(1) hover lookup
                                        setTimeout(() => {
                                            if (_this.sahip?.sahne?.anaGosterim?.secililer) {
                                                _this.sahip.sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                                            }
                                        }, 200);
                                    }
                                })
                            } else
                                /** BİLGİ:  Wrl İmportları */
                                if (dosya.name.indexOf(".wrl") > -1 || dosya.name.indexOf(".FikoWrl1") > -1) {
                                    const importer = new FikoImporterWRL(_this.sahip.sahne);
                                    importer.onProgress = _this.onProgress;
                                    importer.alDosyadan1(dosya).then((_rslt: { yuzeyler: any, cizgiler: any, vertex: any }) => {
                                        const cizim = new FikoCizimYalin(_this.sahip.sahne, dosya.name.replace(".FikoWrl", "").replace(".wrl", ""), _rslt.yuzeyler, _rslt.cizgiler, _rslt.vertex);
                                        _this.sahip.ekle(cizim);

                                        // Sahneyi güncelle
                                        if (_this.sahip?.sahne?.anaGosterim) {
                                            _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                            _this.sahip.sahne.anaGosterim.ciz();

                                            // 🚀 SPATIAL INDEX: Register edges for O(1) hover lookup
                                            setTimeout(() => {
                                                if (_this.sahip?.sahne?.anaGosterim?.secililer) {
                                                    _this.sahip.sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                                                }
                                            }, 200);
                                        }
                                    })

                                    // Yeni kullanım (ürün ağacı isimleri ile birlikte):
                                    // importer.alDosyadanHiyerarsi(dosya).then(_rslt => {
                                    //     console.log("Hiyerarşi ve isimler:", _rslt.hiyerarsi);
                                    // });
                                } else
                                    /** BİLGİ:  FikoApt İmportları */
                                    if (dosya.name.indexOf(".FikoAptv1") > -1) {
                                        let importer = new FikoImporterAPTv1(_this.sahip.sahne);
                                        importer.alDosyadan(dosya).then((_rslt: any) => {
                                            const apt = new FikoAPTv1(_this.sahip.sahne, dosya.name.replace(".FikoAptv1", ""), _rslt);

                                            _this.sahip.ekle(apt);

                                            _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                            _this.sahip.sahne.anaGosterim.ciz();
                                            // resolve(apt);
                                            importer = undefined;
                                        })

                                    } else
                                        /** BİLGİ:  FikoApt İmportları */
                                        if (dosya.name.indexOf(".apt") > -1 || dosya.name.indexOf(".FikoApt") > -1) {
                                            const importer = new FikoImporterAPTCAT(_this.sahip.sahne);
                                            importer.alDosyadan(dosya).then((_rslt: any) => {
                                                const apt = new FikoAPTCAT(_this.sahip.sahne, dosya.name.replace(".FikoApt", "").replace(".aptsource", ""));

                                                apt.tarihi = _rslt.tarihi;
                                                apt.programIsmi = _rslt.programIsmi;
                                                apt.kureMerkezi = _rslt.kureMerkezi;
                                                apt.operasyonIsmi = _rslt.operasyonIsmi;
                                                apt.eksenKoord = _rslt.eksenKoord;
                                                apt.aktiviteler = _rslt.aktiviteler;
                                                apt.eksen = _rslt.eksen;
                                                apt.takimYolu = _rslt.takimYolu;

                                                apt.ekle(_rslt.takimYolu)

                                                // _this.sahip.name = apt.programIsmi;

                                                _this.sahip.ekle(apt);

                                                _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                                _this.sahip.sahne.anaGosterim.ciz();

                                                // console.log(apt);

                                            })

                                        }
                                        else
                                            /** BİLGİ:  FikoApt İmportları */
                                            if (dosya.name.indexOf(".FikoOpGLv1") > -1) {
                                                let importer = new FikoImporterFikoOpGLv1(_this.sahip.sahne);
                                                importer.alDosyadan(dosya).then((_rslt: any) => {

                                                    const fikOperasyon = new FikoOperasyon(_this.sahip.sahne, _rslt.ismi);
                                                    fikOperasyon.userData['aciklama'] = _rslt.aciklama;
                                                    fikOperasyon.islemeEkseni.konumAyarlaJson(_rslt.islemeEkseni);

                                                    const cizimGrubu = new FikoGrupCizim(fikOperasyon.sahne);
                                                    fikOperasyon.cizimler.ekle(cizimGrubu);

                                                    for (let key in _rslt.cizimler) {
                                                        let value = _rslt.cizimler[key];
                                                        const importer = new FikoImporterWRL(fikOperasyon.sahne);
                                                        importer.onProgress = _this.onProgress;
                                                        importer.parcala(value).then((_rslt: { yuzeyler: any, cizgiler: any, vertex: any }[]) => {
                                                            const cizim = new FikoCizimYalin(fikOperasyon.sahne, key, _rslt[0].yuzeyler, _rslt[0].cizgiler, _rslt[0].vertex);
                                                            cizimGrubu.ekle(cizim);
                                                        })
                                                    }

                                                    for (let prgi = 0; prgi < _rslt.programlar.length; prgi++) {
                                                        const prgJson = _rslt.programlar[prgi];

                                                        if (prgJson.aktiviteler.length > 0) {

                                                            let importer1 = new FikoImporterAPTv1(_this.sahip.sahne);
                                                            const __rslt = importer1.parcala(prgJson.apt)
                                                            // console.log(__rslt,prgJson.apt)
                                                            const apt = new FikoAPTv1(fikOperasyon.sahne, prgJson.ismi, __rslt);
                                                            apt.userData['aciklama'] = prgJson.aciklama;
                                                            fikOperasyon.programlar.ekle(apt);

                                                            // _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                                            // _this.sahip.sahne.anaGosterim.ciz();
                                                            // resolve(apt);
                                                            // importer1 = undefined;

                                                        } else {
                                                            const prg = new Fikogram(fikOperasyon.sahne, prgJson.ismi)
                                                            prg.userData['aciklama'] = prgJson.aciklama;
                                                            fikOperasyon.programlar.ekle(prg)
                                                        }
                                                    }



                                                    _this.sahip.ekle(fikOperasyon)

                                                    _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                                    _this.sahip.sahne.anaGosterim.ciz();
                                                    // resolve(apt);
                                                    importer = undefined;
                                                })

                                            } else
                                                /** BİLGİ:  FikoOPGL İmportları */
                                                if (dosya.name.indexOf(".FikoOpGL") > -1) {
                                                    const importer = new FikoImporterFikoOpGL(_this.sahip.sahne);

                                                    importer.alDosyadan(dosya).then((_rslt: any) => {
                                                        // const op = new FikoAPTCAT(_this.sahip.sahne, dosya.name.replace(".FikoApt", "").replace(".aptsource", ""));

                                                        // op.ekle(_rslt.takimYolu)

                                                        // // _this.sahip.name = apt.programIsmi;

                                                        _this.sahip.ekle(_rslt);

                                                        _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                                        _this.sahip.sahne.anaGosterim.ciz();

                                                        // console.log(_rslt);

                                                    })

                                                } else
                                                    /** BİLGİ:  FikoOp İmportları (.FikoOp - gzip veya plain JSON) */
                                                    if (dosya.name.endsWith('.FikoOp')) {
                                                        dosya.arrayBuffer().then(async (buffer: ArrayBuffer) => {
                                                            try {
                                                                let jsonStr: string;
                                                                const bytes = new Uint8Array(buffer);
                                                                if (bytes.length >= 2 && bytes[0] === 0x1F && bytes[1] === 0x8B) {
                                                                    const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream('gzip'));
                                                                    jsonStr = await new Response(stream).text();
                                                                } else {
                                                                    jsonStr = new TextDecoder().decode(buffer);
                                                                }

                                                                const json = JSON.parse(jsonStr);
                                                                const operasyon = FikoOperasyon.fromJson(_this.sahip?.sahne, _this.sahip, json);
                                                                _this.sahip.ekle(operasyon);

                                                                if (_this.sahip?.sahne?.anaGosterim) {
                                                                    _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                                                    _this.sahip.sahne.anaGosterim.ciz();
                                                                }

                                                                // Kamera durumunu uygula (kaydedildigi gibi gorunsun)
                                                                setTimeout(() => {
                                                                    operasyon.kameraDurumuUygula();
                                                                }, 100);

                                                                resolve({ name: dosya.name, size: dosya.size, type: dosya.type });
                                                            } catch (e) {
                                                                console.error('[FikoImporter] FikoOp import hatasi:', e);
                                                                reject(e);
                                                            }
                                                        });
                                                    } else
                                                    /** BİLGİ:  FikoSahne İmportları (.FikoSahne - binary container veya gzip JSON) */
                                                    if (dosya.name.endsWith('.FikoSahne')) {
                                                        dosya.arrayBuffer().then(async (buffer: ArrayBuffer) => {
                                                            try {
                                                                _this.onProgress?.('Sahne dosyası açılıyor...', 10);

                                                                // Gzip decompress
                                                                let rawBytes: Uint8Array;
                                                                const fileBytes = new Uint8Array(buffer);
                                                                if (fileBytes.length >= 2 && fileBytes[0] === 0x1F && fileBytes[1] === 0x8B) {
                                                                    const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream('gzip'));
                                                                    rawBytes = new Uint8Array(await new Response(stream).arrayBuffer());
                                                                } else {
                                                                    rawBytes = fileBytes;
                                                                }

                                                                _this.onProgress?.('Sahne verileri ayrıştırılıyor...', 25);

                                                                let json: any;
                                                                let binBuffers: Uint8Array[] | undefined;

                                                                // Binary container format algılama: "FIKOSAHNE" magic
                                                                const magic = new TextDecoder().decode(rawBytes.subarray(0, 9));
                                                                if (magic === 'FIKOSAHNE') {
                                                                    // Binary container: MAGIC(9) + VER(1) + JSON_LEN(4) + JSON + BUF_COUNT(4) + buffers
                                                                    const view = new DataView(rawBytes.buffer, rawBytes.byteOffset, rawBytes.byteLength);
                                                                    let offset = 10; // magic(9) + version(1)

                                                                    const jsonLen = view.getUint32(offset, true); offset += 4;
                                                                    const jsonStr = new TextDecoder().decode(rawBytes.subarray(offset, offset + jsonLen));
                                                                    offset += jsonLen;
                                                                    json = JSON.parse(jsonStr);

                                                                    const bufCount = view.getUint32(offset, true); offset += 4;
                                                                    binBuffers = [];
                                                                    for (let i = 0; i < bufCount; i++) {
                                                                        const bufLen = view.getUint32(offset, true); offset += 4;
                                                                        binBuffers.push(rawBytes.slice(offset, offset + bufLen));
                                                                        offset += bufLen;
                                                                    }
                                                                } else {
                                                                    // Eski format: pure JSON (v2.0.0 base64 veya daha eski)
                                                                    json = JSON.parse(new TextDecoder().decode(rawBytes));
                                                                }

                                                                const sahne = _this.sahip?.sahne;

                                                                // Çizimleri yükle
                                                                if (json.cizimler && sahne) {
                                                                    _this.onProgress?.('Çizimler yükleniyor...', 40);
                                                                    const cizimler = FikoCizimler.fromJson(sahne, null, json.cizimler, binBuffers);
                                                                    if (cizimler?.children) {
                                                                        for (const child of [...cizimler.children]) {
                                                                            (sahne as any).cizimler?.ekle(child);
                                                                        }
                                                                    }
                                                                }

                                                                // Operasyonları yükle
                                                                if (json.operasyonlar?.operasyonlar && sahne) {
                                                                    _this.onProgress?.('Operasyonlar yükleniyor...', 60);
                                                                    for (const opJson of json.operasyonlar.operasyonlar) {
                                                                        const operasyon = FikoOperasyon.fromJson(sahne, (sahne as any).operasyonlar, opJson, binBuffers);
                                                                        (sahne as any).operasyonlar?.ekle(operasyon);
                                                                    }
                                                                }

                                                                // Konumları yükle
                                                                if (json.konumlar && sahne && (sahne as any).kaynakca?.konumlar) {
                                                                    _this.onProgress?.('Kamera konumları yükleniyor...', 70);
                                                                    (sahne as any).kaynakca.konumlar.fromJson(json.konumlar);
                                                                }

                                                                // Etiketleri yükle
                                                                if (json.etiketler && sahne && (sahne as any).fikoEtiketler) {
                                                                    _this.onProgress?.('Etiketler yükleniyor...', 80);
                                                                    (sahne as any).fikoEtiketler.fromJson(json.etiketler);
                                                                }

                                                                _this.onProgress?.('Sahne oluşturuluyor...', 90);
                                                                if (sahne?.anaGosterim) {
                                                                    sahne.anaGosterim.FikoYT.degisti.emit();
                                                                    sahne.anaGosterim.ciz();

                                                                    setTimeout(() => {
                                                                        if (sahne.anaGosterim?.secililer) {
                                                                            sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                                                                        }
                                                                    }, 200);
                                                                }

                                                                // Kamera durumunu uygula
                                                                if (json.kameraDurumu && sahne?.anaGosterim) {
                                                                    setTimeout(() => {
                                                                        const kamera = sahne.anaGosterim.aktifKamera;
                                                                        const kontrol = sahne.anaGosterim.aktifKontrol;
                                                                        if (kamera && json.kameraDurumu.position) {
                                                                            kamera.position.fromArray(json.kameraDurumu.position);
                                                                            if (json.kameraDurumu.quaternion) kamera.quaternion.fromArray(json.kameraDurumu.quaternion);
                                                                            if (json.kameraDurumu.up) kamera.up.fromArray(json.kameraDurumu.up);
                                                                            if (json.kameraDurumu.zoom) kamera.zoom = json.kameraDurumu.zoom;
                                                                            kamera.updateProjectionMatrix();
                                                                        }
                                                                        if (kontrol && json.kameraDurumu.hedef) {
                                                                            kontrol.target.fromArray(json.kameraDurumu.hedef);
                                                                            kontrol.update();
                                                                        }
                                                                        sahne.anaGosterim.ciz();
                                                                    }, 100);
                                                                } else if (sahne?.anaGosterim && (sahne as any).ekranaSigdir) {
                                                                    setTimeout(() => {
                                                                        (sahne as any).ekranaSigdir();
                                                                    }, 300);
                                                                }

                                                                // Parametrik modelleme (feature geçmişi) geri yükle
                                                                if (json.modelleme && (sahne?.anaGosterim as any)?.modellemeDeserialize) {
                                                                    setTimeout(() => { try { (sahne.anaGosterim as any).modellemeDeserialize(json.modelleme, binBuffers); } catch (e) { console.warn('[FikoImporter] modelleme yükleme hatası', e); } }, 150);
                                                                }

                                                                // Kalıcı ölçümleri geri yükle (nokta/tip kayıtlarından yeniden kur)
                                                                if (json.olcumler && (sahne?.anaGosterim as any)?.olcumDeserialize) {
                                                                    setTimeout(() => { try { (sahne.anaGosterim as any).olcumDeserialize(json.olcumler); } catch (e) { console.warn('[FikoImporter] ölçüm yükleme hatası', e); } }, 300);
                                                                }

                                                                _this.onProgress?.('Sahne yüklendi!', 100);
                                                                resolve({ name: dosya.name, size: dosya.size, type: dosya.type });
                                                            } catch (e) {
                                                                console.error('[FikoImporter] FikoSahne import hatasi:', e);
                                                                _this.onProgress?.('Sahne yükleme hatası!', 0);
                                                                reject(e);
                                                            }
                                                        });
                                                    } else
                                                    /** BİLGİ:  Point Cloud İmportları (PLY, XYZ, PTS, ASC, PCD) */
                                                    if (POINT_CLOUD_FORMATS.some(ext => dosya.name.toLowerCase().endsWith(ext))) {
                                                        const importer = new FikoImporterPointCloud(_this.sahip.sahne);
                                                        importer.onProgress = _this.onProgress;

                                                        importer.yukle(dosya).then((pointCloudGrup) => {
                                                            if (pointCloudGrup) {
                                                                // Ürün ağacına ekle (3D çizimler grubuna)
                                                                const urunAgaci = (_this.sahip.sahne as any).urunAgaci;
                                                                if (urunAgaci) {
                                                                    // "Fiko Çizimler" grubunu bul
                                                                    let cizimGrup = urunAgaci.children?.find((c: any) => c.name === 'Fiko Çizimler');
                                                                    if (cizimGrup) {
                                                                        cizimGrup.add(pointCloudGrup);
                                                                        // console.log('[FikoImporter] Point Cloud "Fiko Çizimler"e eklendi:', pointCloudGrup.name);
                                                                    } else {
                                                                        // Fiko Çizimler yoksa urunAgaci'na ekle
                                                                        urunAgaci.add(pointCloudGrup);
                                                                    }
                                                                } else {
                                                                    // urunAgaci yoksa sahip objesine ekle
                                                                    _this.sahip.ekle(pointCloudGrup);
                                                                }

                                                                // Sahneyi güncelle
                                                                if (_this.sahip?.sahne?.anaGosterim) {
                                                                    _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                                                    _this.sahip.sahne.anaGosterim.ciz();

                                                                    // Model yüklendikten sonra otomatik ekrana sığdır
                                                                    setTimeout(() => {
                                                                        if (_this.sahip.sahne && typeof _this.sahip.sahne.ekranaSigdir === 'function') {
                                                                            _this.sahip.sahne.ekranaSigdir(pointCloudGrup);
                                                                        }
                                                                    }, 100);
                                                                }

                                                                resolve({ name: dosya.name, size: dosya.size, type: dosya.type });
                                                            } else {
                                                                console.error('[FikoImporter] Point cloud yüklenemedi');
                                                                reject(new Error('Point cloud yüklenemedi'));
                                                            }
                                                        }).catch((error) => {
                                                            console.error('[FikoImporter] Point cloud import hatası:', error);
                                                            reject(error);
                                                        });
                                                    }
    }

    /**
     * Çoklu dosya yükleme (birden fazla dosya seçildiğinde)
     * Tüm dosyaları sırayla veya paralel olarak import eder
     */
    private async cokluDosyaYukle(dosyalar: File[], resolve: (value: any) => void, reject: (reason: any) => void): Promise<void> {
        const _this = this;
        const sonuclar: any[] = [];
        const hatalar: Error[] = [];

        // Progress callback ayarla
        if (!_this.onProgress && _this.sahip?.sahne?.anaGosterim) {
            const anaGosterim = _this.sahip.sahne.anaGosterim;
            _this.onProgress = (mesaj: string, progress: number) => {
                anaGosterim.yuklemeDurumu.aktif = true;
                anaGosterim.yuklemeDurumu.mesaj = mesaj;
                anaGosterim.yuklemeDurumu.progress = progress;
                setTimeout(() => {
                    try { anaGosterim.CD.detectChanges(); } catch (e) { }
                }, 0);
                if (progress >= 100) {
                    setTimeout(() => {
                        anaGosterim.yuklemeDurumu.aktif = false;
                        try { anaGosterim.CD.detectChanges(); } catch (e) { }
                    }, 1000);
                }
            };
        }

        // STP dosyalarını filtrele
        const stpDosyalar = dosyalar.filter(d =>
            d.name.toLowerCase().endsWith('.stp') || d.name.toLowerCase().endsWith('.step')
        );
        const digerDosyalar = dosyalar.filter(d =>
            !d.name.toLowerCase().endsWith('.stp') && !d.name.toLowerCase().endsWith('.step')
        );

        // STP dosyaları için toplu dialog göster
        if (stpDosyalar.length > 0) {
            const dialogService = _this.sahip?.sahne?.anaGosterim?.DialogFiko ?? null;
            await _this.cokluStpImport(stpDosyalar, dialogService, sonuclar, hatalar);
        }

        // Diğer dosyaları sırayla yükle
        for (let i = 0; i < digerDosyalar.length; i++) {
            const dosya = digerDosyalar[i];
            _this.onProgress?.(`Yükleniyor: ${dosya.name} (${i + 1}/${digerDosyalar.length})`, ((i + 1) / digerDosyalar.length) * 100);

            try {
                await new Promise<void>((res, rej) => {
                    _this.tekDosyaYukleDirect(dosya, (result: any) => {
                        if (result) sonuclar.push(result);
                        res();
                    }, (err: Error) => {
                        hatalar.push(err);
                        res(); // Hata olsa da devam et
                    });
                });
            } catch (err: any) {
                hatalar.push(err);
            }
        }

        // Sahneyi güncelle
        if (_this.sahip?.sahne?.anaGosterim) {
            _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
            _this.sahip.sahne.anaGosterim.ciz();

            // SPATIAL INDEX
            setTimeout(() => {
                if (_this.sahip?.sahne?.anaGosterim?.secililer) {
                    _this.sahip.sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                }
            }, 200);
        }

        _this.onProgress?.(`${sonuclar.length} dosya yüklendi`, 100);

        if (hatalar.length > 0) {
            console.warn('[FikoImporter] Bazı dosyalar yüklenemedi:', hatalar);
        }

        resolve({ yuklenen: sonuclar.length, hatali: hatalar.length, sonuclar });
    }

    /**
     * Çoklu STP dosyası import (tek dialog ile)
     */
    private async cokluStpImport(dosyalar: File[], dialogService: any, sonuclar: any[], hatalar: Error[]): Promise<void> {
        const _this = this;
        _this.stpImporter.onProgress = _this.onProgress;

        if (dialogService) {
            // Dialog ile çoklu import
            return new Promise((resolve) => {
                import('./stp-import-dialog').then(({ StpImportDialogComponent }) => {
                    let isResolved = false;

                    const onImport = async (options: STPImportOptions) => {
                        if (isResolved) return;
                        isResolved = true;

                        // Dialog'dan gelen tessellation kalitesini importer'a aktar (occt-import-js icin)
                        if (options.importMethod === 'occt-import-js' && options.linearDeflection != null) {
                            _this.stpImporter.tessellationKalitesi = {
                                linearDeflection: options.linearDeflection,
                                angularDeflection: options.angularDeflection,
                            };
                        }

                        // Tüm STP dosyalarını sırayla import et
                        for (let i = 0; i < dosyalar.length; i++) {
                            const dosya = dosyalar[i];
                            _this.onProgress?.(`STP Import: ${dosya.name} (${i + 1}/${dosyalar.length})`, ((i + 1) / dosyalar.length) * 100);

                            try {
                                let result;
                                if (options.importMethod === 'opencascade-js') {
                                    result = await _this.stpImporter.importWithOpenCascade(dosya, options);
                                } else {
                                    result = await _this.stpImporter.yukle(dosya, options.gruplama, options);
                                }

                                if (result) {
                                    if (Array.isArray(result)) {
                                        result.forEach(item => _this.sahip.ekle(item));
                                    } else {
                                        _this.sahip.ekle(result);
                                    }
                                    sonuclar.push({ name: dosya.name, result });
                                }
                            } catch (err: any) {
                                console.error(`[FikoImporter] STP import hatası (${dosya.name}):`, err);
                                hatalar.push(err);
                            }
                        }

                        resolve();
                    };

                    const onCancel = () => {
                        if (isResolved) return;
                        isResolved = true;
                        resolve();
                    };

                    try {
                        dialogService.yeniAc({
                            bilesen: StpImportDialogComponent,
                            parametreler: {
                                dosya: dosyalar[0], // İlk dosyayı göster
                                dosyaSayisi: dosyalar.length, // Toplam dosya sayısı
                                dosyaListesi: dosyalar.map(d => d.name), // Dosya isimleri
                                hasColorSupport: false,
                                onImport: onImport,
                                onCancel: onCancel
                            },
                            ayarlar: {
                                baslik: `STP Import (${dosyalar.length} dosya)`,
                                genislik: '400px',
                                yukseklik: 'auto'
                            }
                        });
                    } catch (err: any) {
                        console.error('[FikoImporter] STP import dialog açılamadı:', err);
                        hatalar.push(err);
                        onCancel();
                    }
                }).catch((err: any) => {
                    console.error('[FikoImporter] stp-import-dialog yüklenemedi:', err);
                    hatalar.push(err);
                    resolve();
                });
            });
        } else {
            // Dialog olmadan sırayla import et
            for (let i = 0; i < dosyalar.length; i++) {
                const dosya = dosyalar[i];
                _this.onProgress?.(`STP Import: ${dosya.name} (${i + 1}/${dosyalar.length})`, ((i + 1) / dosyalar.length) * 100);

                try {
                    const result = await _this.stpImporter.yukle(dosya, 'yok');
                    if (result) {
                        if (Array.isArray(result)) {
                            result.forEach(item => _this.sahip.ekle(item));
                        } else {
                            _this.sahip.ekle(result);
                        }
                        sonuclar.push({ name: dosya.name, result });
                    }
                } catch (err: any) {
                    hatalar.push(err);
                }
            }
        }
    }

    /**
     * Tek dosya yükleme (STP hariç - çoklu import için yardımcı)
     */
    private tekDosyaYukleDirect(dosya: File, resolve: (value: any) => void, reject: (reason: any) => void): void {
        const _this = this;

        // WRL
        if (dosya.name.indexOf(".wrl2") > -1 || dosya.name.indexOf(".FikoWrl") > -1) {
            const importer = new FikoImporterWRL(_this.sahip.sahne);
            importer.onProgress = _this.onProgress;
            importer.alDosyadan(dosya).then((_rslt: { yuzeyler: any, cizgiler: any, vertex: any }[]) => {
                for (let i = 0; i < _rslt.length; i++) {
                    const e = _rslt[i];
                    const cizim = new FikoCizimYalin(_this.sahip.sahne, dosya.name.replace(".FikoWrl", "").replace(".wrl", ""), e.yuzeyler, e.cizgiler, e.vertex);
                    cizim.name += "." + (i + 1);
                    _this.sahip.ekle(cizim);
                }
                resolve({ name: dosya.name });
            }).catch(reject);
        }
        // JSON
        else if (dosya.name.indexOf(".FikoJson") > -1 || dosya.name.indexOf(".json") > -1) {
            const importer = new FikoImporterFikoJson(_this.sahip.sahne);
            importer.onProgress = _this.onProgress;
            importer.alDosyadan1(dosya).then((_rslt: { yuzeyler: any, cizgiler: any, vertex: any }) => {
                const cizim = new FikoCizimYalin(_this.sahip.sahne, dosya.name.replace(".FikoJson", "").replace(".json", ""), _rslt.yuzeyler, _rslt.cizgiler, _rslt.vertex);
                _this.sahip.ekle(cizim);
                resolve({ name: dosya.name });
            }).catch(reject);
        }
        // Point Cloud
        else if (POINT_CLOUD_FORMATS.some(ext => dosya.name.toLowerCase().endsWith(ext))) {
            const importer = new FikoImporterPointCloud(_this.sahip.sahne);
            importer.onProgress = _this.onProgress;
            importer.yukle(dosya).then((pointCloudGrup) => {
                if (pointCloudGrup) {
                    const urunAgaci = (_this.sahip.sahne as any).urunAgaci;
                    if (urunAgaci) {
                        let cizimGrup = urunAgaci.children?.find((c: any) => c.name === 'Fiko Çizimler');
                        if (cizimGrup) {
                            cizimGrup.add(pointCloudGrup);
                        } else {
                            urunAgaci.add(pointCloudGrup);
                        }
                    } else {
                        _this.sahip.ekle(pointCloudGrup);
                    }
                    resolve({ name: dosya.name });
                } else {
                    reject(new Error('Point cloud yüklenemedi'));
                }
            }).catch(reject);
        }
        // Desteklenmeyen format
        else {
            console.warn('[FikoImporter] Desteklenmeyen format:', dosya.name);
            resolve(null);
        }
    }

    /**
     * STP dosyası import işlemini yönetir (dialog ile veya doğrudan)
     */
    private processStpImport(dosya: File, dialogService: any, resolve: (value: any) => void, reject: (reason: any) => void): void {
        const _this = this;

        if (dialogService) {
            // Dialog ile import et
            _this.stpImportWithDialog(dosya, dialogService).then((result) => {
                if (result) {
                    // Sahneye ekle - Array ise tek tek ekle
                    if (Array.isArray(result)) {
                        result.forEach(item => _this.sahip.ekle(item));
                    } else {
                        _this.sahip.ekle(result);
                    }

                    // Sahneyi güncelle
                    if (_this.sahip?.sahne?.anaGosterim) {
                        _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                        _this.sahip.sahne.anaGosterim.ciz();

                        // Model yüklendikten sonra otomatik ekrana sığdır
                        setTimeout(() => {
                            if (_this.sahip.sahne && typeof _this.sahip.sahne.ekranaSigdir === 'function') {
                                // Array ise ilk elemanı veya tüm sahneyi sığdır
                                const objToFit = Array.isArray(result) ? result[0] : result;
                                if (objToFit) {
                                    _this.sahip.sahne.ekranaSigdir(objToFit);
                                }
                            }
                        }, 100);

                        // SPATIAL INDEX: Register edges for O(1) hover lookup
                        setTimeout(() => {
                            if (_this.sahip?.sahne?.anaGosterim?.secililer) {
                                _this.sahip.sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                            }
                        }, 200);
                    }

                    resolve({ name: dosya.name, size: dosya.size, type: dosya.type });
                } else {
                    // Dialog iptal edildi
                    resolve(null);
                }
            }).catch(error => {
                alert(`STEP dosyası yüklenemedi: ${error.message}`);
                reject(error);
            });
        } else {
            // Dialog servisi yok, eski yöntemle devam et
            _this.stpImportDefault(dosya).then((result) => {
                if (result) {
                    // Sahneye ekle - Array ise tek tek ekle
                    if (Array.isArray(result)) {
                        result.forEach(item => _this.sahip.ekle(item));
                    } else {
                        _this.sahip.ekle(result);
                    }

                    if (_this.sahip?.sahne?.anaGosterim) {
                        _this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                        _this.sahip.sahne.anaGosterim.ciz();

                        setTimeout(() => {
                            if (_this.sahip.sahne && typeof _this.sahip.sahne.ekranaSigdir === 'function') {
                                const objToFit = Array.isArray(result) ? result[0] : result;
                                if (objToFit) {
                                    _this.sahip.sahne.ekranaSigdir(objToFit);
                                }
                            }
                        }, 100);

                        setTimeout(() => {
                            if (_this.sahip?.sahne?.anaGosterim?.secililer) {
                                _this.sahip.sahne.anaGosterim.secililer.registerEdgesForSpatialIndex();
                            }
                        }, 200);
                    }

                    resolve({ name: dosya.name, size: dosya.size, type: dosya.type });
                }
            }).catch(error => {
                alert(`STEP dosyası yüklenemedi: ${error.message}`);
                reject(error);
            });
        }
    }

    /**
     * STP dosyasını dialog ile import et
     * Dialog'da kullanıcı import yöntemini ve parametrelerini seçer
     *
     * NOT: OpenCascade.js dialog açılırken YÜKLENMİYOR.
     * Sadece "Import" butonuna tıklandığında ve "opencascade-js" seçili ise yüklenir.
     * Bu sayede 65.9MB'lık WASM dosyası gereksiz yere indirilmez.
     */
    private async stpImportWithDialog(dosya: File, dialogService: any): Promise<any> {
        // OpenCascade.js artık dialog açılırken YÜKLENMİYOR
        // Sadece import sırasında ve opencascade-js seçiliyse yüklenecek
        const _this = this;

        return new Promise((resolve) => {
            // Dynamic import for dialog component
            import('./stp-import-dialog').then(({ StpImportDialogComponent }) => {
                // Callback-based approach - ngComponentOutlet @Output desteklemediği için
                // parametreler üzerinden callback geçiriyoruz
                let isResolved = false;

                const onImport = async (options: STPImportOptions) => {
                    if (isResolved) return;
                    isResolved = true;

                    // Dialog'dan gelen tessellation kalitesini importer'a aktar (occt-import-js icin)
                    if (options.importMethod === 'occt-import-js' && options.linearDeflection != null) {
                        _this.stpImporter.tessellationKalitesi = {
                            linearDeflection: options.linearDeflection,
                            angularDeflection: options.angularDeflection,
                        };
                    }

                    try {
                        _this.onProgress?.('Import başlıyor...', 0);
                        let result;
                        if (options.importMethod === 'opencascade-js') {
                            // OpenCascade.js SADECE BURADA yükleniyor (lazy loading)
                            _this.onProgress?.('OpenCascade.js yükleniyor (65.9 MB)...', 5);
                            result = await _this.stpImporter.importWithOpenCascade(dosya, options);
                        } else {
                            result = await _this.stpImporter.yukle(dosya, options.gruplama, options);
                        }
                        _this.onProgress?.('Import tamamlandı!', 100);
                        resolve(result);
                    } catch (e: any) {
                        resolve(null);
                    }
                };

                const onCancel = () => {
                    if (isResolved) return;
                    isResolved = true;
                    resolve(null);
                };

                try {
                    dialogService.yeniAc({
                        bilesen: StpImportDialogComponent,
                        parametreler: {
                            dosya: dosya,
                            // hasColorSupport artık false - OpenCascade yüklenmeden bilinemiyor
                            // Kullanıcı isterse opencascade-js seçebilir, o zaman yüklenecek
                            hasColorSupport: false,
                            onImport: onImport,
                            onCancel: onCancel
                        },
                        ayarlar: {
                            baslik: 'STP Import Secenekleri',
                            genislik: '380px',
                            yukseklik: 'auto'
                        }
                    });
                } catch (err: any) {
                    console.error('[FikoImporter] STP import dialog açılamadı:', err);
                    onCancel();
                }
            }).catch((err: any) => {
                console.error('[FikoImporter] stp-import-dialog yüklenemedi:', err);
                resolve(null);
            });
        });
    }

    /**
     * STP dosyasını varsayılan yöntemle import et (dialog olmadan)
     * Dosya boyutuna gore adaptif tessellation kalitesi uygular.
     */
    private async stpImportDefault(dosya: File): Promise<any> {
        // Dialog olmadan import edilirken dosya boyutuna gore adaptif kalite sec
        this.stpImporter.tessellationKalitesiAyarla('adaptif', dosya.size);
        return this.stpImporter.yukle(dosya, 'yok');
    }

}